"use client";

import Link from "next/link";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { ExternalLink, MapPin, Clock } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { routes } from "@/lib/routes";

const WHATSAPP_URL = "https://wa.me/5492944636295";
const GOOGLE_MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Eduardo+Sfeir+2827+El+Bolsón+Río+Negro+Argentina";

export default function ContactPage() {
  const { t } = useTranslation();

  return (
    <div className="bg-surface-muted min-h-screen pb-24">
      {/* Hero — aligned with pricing page treatment */}
      <section className="relative flex min-h-[220px] flex-col items-center justify-center overflow-hidden px-4 sm:min-h-[280px] md:min-h-[300px]">
        <div className="absolute inset-0 bg-slate-950" aria-hidden />
        <div
          className="absolute inset-0 opacity-[0.45]"
          style={{
            backgroundImage: "url('/new/DSC08873.JPG')",
            backgroundPosition: "center 40%",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_90%_80%_at_50%_20%,transparent_0%,rgba(15,23,42,0.92)_65%,rgb(15,23,42)_100%)]"
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-linear-to-b from-slate-950/30 via-slate-950/70 to-slate-950"
          aria-hidden
        />

        <div className="relative z-10 mx-auto max-w-3xl px-2 pt-6 text-center sm:pt-8">
          <p className="mx-auto mb-5 inline-flex max-w-2xl items-center justify-center rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[13px] font-medium leading-snug text-white/95 shadow-sm backdrop-blur-md sm:text-sm">
            {t("contact.directContact")}
          </p>
          <h1 className="text-[1.75rem] font-semibold tracking-[-0.02em] text-white sm:text-4xl md:text-5xl">
            {t("contact.title")}
          </h1>
          <nav
            className="mt-5 flex items-center justify-center gap-2 text-[13px] font-medium text-slate-400"
            aria-label="Breadcrumb"
          >
            <Link href={routes.home} className="transition-colors hover:text-white">
              {t("contact.breadcrumbHome")}
            </Link>
            <span className="text-slate-600" aria-hidden>
              —
            </span>
            <span className="text-slate-200">{t("contact.breadcrumbContact")}</span>
          </nav>
        </div>
      </section>

      <div className="relative z-20 mx-auto -mt-9 w-full max-w-6xl px-4 sm:-mt-10 sm:px-6">
        <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_8px_40px_-12px_rgba(15,23,42,0.12)] sm:rounded-3xl">
          <div className="border-b border-slate-100 bg-linear-to-b from-slate-50/80 to-white px-6 py-10 sm:px-10 sm:py-12 md:px-14 md:py-14">
            <p className="mx-auto max-w-2xl text-center text-base leading-relaxed text-slate-600 sm:text-lg">
              {t("contact.subtitle")}
            </p>
          </div>

          <div className="grid gap-px bg-slate-100 sm:grid-cols-2 lg:grid-cols-3">
            {/* Location */}
            <article className="group flex flex-col bg-white p-6 transition-colors hover:bg-slate-50/80 sm:p-8">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary ring-1 ring-primary/10 transition group-hover:ring-primary/25">
                <MapPin className="h-5 w-5" strokeWidth={2} />
              </div>
              <h2 className="mt-5 text-[15px] font-semibold tracking-tight text-slate-900 sm:text-base">
                {t("footer.ourLocation")}
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{t("contact.address")}</p>
              <p className="mt-1 text-sm text-slate-500">{t("contact.landmark")}</p>
              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition hover:text-primary-strong"
              >
                {t("contact.openInMaps")}
                <ExternalLink className="h-3.5 w-3.5 opacity-70" strokeWidth={2} />
              </a>
            </article>

            {/* Hours */}
            <article className="group flex flex-col bg-white p-6 transition-colors hover:bg-slate-50/80 sm:p-8">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 ring-1 ring-slate-200/80 transition group-hover:bg-slate-900 group-hover:text-white group-hover:ring-slate-900">
                <Clock className="h-5 w-5" strokeWidth={2} />
              </div>
              <h2 className="mt-5 text-[15px] font-semibold tracking-tight text-slate-900 sm:text-base">
                {t("footer.weAreOpen")}
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{t("footer.hours")}</p>
            </article>

            {/* WhatsApp — full width on sm grid last row, single column on lg */}
            <article className="group flex flex-col bg-white p-6 sm:col-span-2 sm:p-8 lg:col-span-1">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/15 transition group-hover:bg-emerald-600 group-hover:text-white group-hover:ring-emerald-700">
                <WhatsAppIcon size={22} className="h-[22px] w-[22px]" />
              </div>
              <h2 className="mt-5 text-[15px] font-semibold tracking-tight text-slate-900 sm:text-base">
                {t("footer.sendMessage")}
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{t("footer.whatsapp")}</p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-600/20 bg-emerald-600 px-5 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:border-emerald-700 hover:bg-emerald-700 hover:shadow-md active:scale-[0.99] sm:max-w-none"
              >
                <WhatsAppIcon size={18} className="h-[18px] w-[18px]" />
                {t("contact.chatWhatsApp")}
              </a>
            </article>
          </div>
        </div>

        {/* Map */}
        <div className="mt-10 sm:mt-12">
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group block overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_8px_40px_-12px_rgba(15,23,42,0.1)] transition hover:border-primary/25 hover:shadow-[0_12px_48px_-12px_rgba(15,23,42,0.15)] sm:rounded-3xl"
          >
            <div className="relative aspect-21/10 w-full overflow-hidden bg-slate-900 sm:aspect-2.4/1">
              <Image
                src="/new/DSC06258.jpeg"
                alt={t("images.contactMap")}
                fill
                sizes="(max-width: 768px) 100vw, 1152px"
                className="object-cover opacity-85 transition duration-500 group-hover:scale-[1.03] group-hover:opacity-90"
                loading="lazy"
                quality={80}
              />
              <div
                className="absolute inset-0 bg-linear-to-t from-slate-950/80 via-slate-950/25 to-slate-950/20"
                aria-hidden
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4 text-center">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/95 px-5 py-2.5 text-sm font-semibold text-slate-900 shadow-lg ring-1 ring-white/60 backdrop-blur-sm transition group-hover:bg-primary group-hover:text-white group-hover:ring-primary/30">
                  <MapPin className="h-4 w-4 shrink-0" strokeWidth={2} />
                  {t("contact.openInMaps")}
                </span>
              </div>
            </div>
            <div className="border-t border-slate-100 bg-slate-50/50 px-6 py-5 sm:px-8 sm:py-6">
              <p className="text-center text-[15px] font-medium text-slate-800">{t("contact.address")}</p>
              <p className="mt-1 text-center text-sm text-slate-500">{t("contact.landmark")}</p>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}
